import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, onSnapshot, doc, updateDoc, getDocs, arrayRemove } from 'firebase/firestore';
import './Users.css';
import UserAvatar from '../common/UserAvatar.jsx';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const currentUser = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        setUsers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => console.error('Ошибка загрузки пользователей:', error)
    );
    return () => unsubscribe();
  }, []);

  // Удалённые видит только админ; заблокированные видны всем
  const visibleUsers = (isAdmin ? users : users.filter((u) => !u.deleted))
    .filter((u) => {
      const q = searchText.trim().toLowerCase();
      if (!q) return true;
      return (
        (u.displayName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    });

  const toggleBlock = async (user) => {
    try {
      await updateDoc(doc(db, 'users', user.id), { blocked: !user.blocked });
    } catch (error) {
      console.error('Ошибка блокировки:', error);
    }
  };

  // Исключение удалённого из ВСЕХ групп
  const removeFromAllGroups = async (uid) => {
    try {
      const snap = await getDocs(collection(db, 'groups'));
      for (const g of snap.docs) {
        const members = g.data().members || [];
        if (members.includes(uid)) {
          await updateDoc(doc(db, 'groups', g.id), { members: arrayRemove(uid) });
        }
      }
    } catch (error) {
      console.error('Ошибка исключения из групп:', error);
    }
  };

  const toggleDelete = async (user) => {
    if (!user.deleted) {
      if (!window.confirm(`Удалить аккаунт «${user.displayName || user.email}»? Пользователь будет отключён и исключён из всех групп. Повторное нажатие восстановит аккаунт.`)) return;
      try {
        await updateDoc(doc(db, 'users', user.id), { deleted: true });
        await removeFromAllGroups(user.id);
      } catch (error) {
        console.error('Ошибка удаления аккаунта:', error);
      }
    } else {
      try {
        await updateDoc(doc(db, 'users', user.id), { deleted: false });
      } catch (error) {
        console.error('Ошибка восстановления аккаунта:', error);
      }
    }
  };

  const changeRole = async (user, role) => {
    try {
      await updateDoc(doc(db, 'users', user.id), { role });
    } catch (error) {
      console.error('Ошибка смены роли:', error);
    }
  };

    const roleLabel = (u) =>
    u.role === 'admin' ? ' · Админ'
    : u.role === 'moderator' ? ' · Модератор'
    : u.role === 'middle' ? ' · Мидл&Boss'
    : u.role === 'newbie' ? ' · Новенький' : '';

  return (
    <div className="users-container">
      <h2>
        Пользователи ({visibleUsers.length}){' '}
        {isAdmin && <span style={{ fontSize: '14px', color: '#4caf50' }}>· режим администратора</span>}
      </h2>

      <input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="🔍 Поиск по имени или email..."
        style={{ width: '100%', padding: '10px 15px', marginBottom: '15px', border: '1px solid #ddd', borderRadius: '20px', boxSizing: 'border-box', background: 'rgba(255,255,255,0.8)' }}
      />

      <div className="users-list">
        {visibleUsers.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center' }}>Никого не нашли.</p>
        ) : (
          visibleUsers.map((user) => (
            <div key={user.id} className="user-card" style={(user.blocked || user.deleted) ? { opacity: 0.7 } : {}}>
             <UserAvatar user={user} size={50} />
              <div className="user-info">
                <h3>{user.displayName || (user.email ? user.email.split('@')[0] : 'Пользователь')}</h3>
                <span className="status online">
                  {user.deleted ? '💀 удалён' : user.blocked ? '🚫 блокировка (просмотр)' : 'Участник сети'}
                  {roleLabel(user)}
                </span>
              </div>

              {user.id === currentUser?.uid ? (
                <span className="me-badge">Это вы</span>
              ) : (
                <>
                  {isAdmin && (
                    <select
                      value={user.role || 'user'}
                      onChange={(e) => changeRole(user, e.target.value)}
                      title="Уровень доступа"
                      style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '12px', background: '#fff', cursor: 'pointer' }}
                    >
                      <option value="newbie">Новенький</option>
                      <option value="user">Пользователь</option>
                      <option value="middle">Мидл&Boss</option>
                      <option value="moderator">Модератор</option>
                      <option value="admin">Администратор</option>
                    </select>
                  )}
                  <button className="write-btn" onClick={() => navigate('/dialogs/' + user.id)} title="Написать личное сообщение">
                    💬 Написать
                  </button>
                  {isAdmin && (
                    <button
                      className={user.blocked ? 'unblock-btn' : 'block-btn'}
                      onClick={() => toggleBlock(user)}
                      title={user.blocked ? 'Разблокировать (вернёт право писать)' : 'Заблокировать (останется право читать)'}
                    >
                      {user.blocked ? '✅ Разблокировать' : '🚫 Заблокировать'}
                    </button>
                  )}
                  {isAdmin && user.id !== currentUser?.uid && (
                    <button
                      onClick={() => toggleDelete(user)}
                      title={user.deleted ? 'Восстановить аккаунт' : 'Удалить аккаунт (отключит и исключит из групп)'}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd',
                        background: user.deleted ? '#4caf50' : '#fff',
                        color: user.deleted ? '#fff' : '#333',
                        cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                      }}
                    >
                      {user.deleted ? '♻️ Восстановить' : '💀 Удалить'}
                    </button>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Users;